<template>
  <div class="display-flex width-percent-100 height-percent-100 group-tenant">
    <!-- 左栏：集团列表 -->
    <div class="left bc-white br-4 padding-20">
      <div class="display-flex ai-center jc-space-between mb-20">
        <b class="fs-18">集团租户</b>
        <el-button type="text" icon="el-icon-plus" @click="addGroup">添加集团</el-button>
      </div>
      <el-input
        v-model="groupSearch"
        size="small"
        placeholder="搜索集团或集团下租户"
        prefix-icon="el-icon-search"
        clearable
        class="mb-20">
      </el-input>
      <div class="group-list">
        <div
          v-for="item in groupList"
          :key="item.id"
          class="group-item display-flex ai-center jc-space-between"
          :class="{ active: currentGroup && item.id === currentGroup.id }"
          @click="changeGroup(item)">
          <div class="display-flex ai-center group-name">
            <span class="group-icon"><i class="el-icon-office-building"></i></span>
            <span class="fs-14 text-ellipsis">{{ item.name }}</span>
          </div>
          <!-- 到期提示：一个月内"N天后过期"/当天"今天过期"/已过期"过期N天"/过期超30天"长期过期" -->
          <el-tag
            v-if="item.expiryLabel"
            size="mini"
            :type="expiryTagType(item.expiryLabel)"
            effect="plain"
            class="ml-6 expiry-tag">
            {{ item.expiryLabel }}
          </el-tag>
          <i
            v-if="currentGroup && item.id === currentGroup.id"
            class="el-icon-check selected-check"
            aria-hidden="true"></i>
        </div>
        <el-empty
          v-if="!groupList.length"
          :image-size="80"
          :description="currentGroup ? '暂无数据' : '请选择一个集团'">
        </el-empty>
      </div>
      <div class="display-flex jc-center mt-20">
        <el-pagination
          hide-on-single-page
          @current-change="handleGroupPageChange"
          :current-page="groupPage"
          :page-size="10"
          layout="prev, pager, next"
          :total="groupTotal">
        </el-pagination>
      </div>
    </div>

    <!-- 中栏：组织树 -->
    <div class="middle bc-white br-4 padding-20 ml-20" :style="{ width: middleWidth }">
      <div class="display-flex ai-center jc-space-between">
        <b class="fs-14">集团架构</b>
        <div class="org-tree-actions">
          <el-button
            v-if="currentGroup"
            type="text"
            icon="el-icon-download"
            @click="openImportDialog">导入架构</el-button>
          <template v-if="currentNode">
            <el-button
              v-if="currentNode.tenantType === 2 && currentNode.id === rootNodeId"
              type="text"
              icon="el-icon-connection"
              @click="supplementParent">补充上级</el-button>
            <el-popover
              v-if="currentNode.tenantType === 2 || currentNode.tenantType === 3"
              v-model="childTypePopoverVisible"
              trigger="hover"
              placement="bottom"
              width="100">
              <div class="child-type-options">
                <el-button type="text" style="display: block; width: 100%; height: 28px; margin: 0; padding: 0 8px; text-align: left" @click="selectChildType('tenant', 'existing')">接入已有租户</el-button>
                <el-button type="text" style="display: block; width: 100%; height: 28px; margin: 0; padding: 0 8px; text-align: left" @click="selectChildType('tenant', 'new')">新建一般租户</el-button>
                <el-button type="text" style="display: block; width: 100%; height: 28px; margin: 0; padding: 0 8px; text-align: left" @click="selectChildType('node')">新建节点租户</el-button>
              </div>
              <el-button slot="reference" type="text" icon="el-icon-plus">新增下级</el-button>
            </el-popover>
            <el-button
              v-if="currentNode.tenantType === 1 || currentNode.tenantType === 3"
              type="text"
              icon="el-icon-sort"
              @click="changeParent">调整所属</el-button>
          </template>
        </div>
      </div>
      <el-input
        v-model="treeFilter"
        size="small"
        placeholder="搜索节点或租户"
        prefix-icon="el-icon-search"
        clearable
        class="mt-20">
      </el-input>
      <el-tree
        ref="orgTree"
        class="org-tree mt-16"
        :key="treeKey"
        :data="treeData"
        :props="treeProps"
        node-key="id"
        highlight-current
        :indent="20"
        :expand-on-click-node="false"
        :default-expanded-keys="expandedKeys"
        @node-click="handleNodeClick"
        @node-expand="handleTreeExpand">
        <template slot-scope="{ node, data }">
          <span class="org-tree-label display-flex ai-center">
            <i v-if="data.tenantType === 2" class="org-tree-icon el-icon-office-building color-primary"></i>
            <i v-else-if="data.tenantType === 3" :class="['org-tree-icon', node.expanded ? 'el-icon-folder-opened' : 'el-icon-folder', 'color-primary']"></i>
            <i v-else class="org-tree-icon el-icon-school color-info"></i>
            <span
              class="org-tree-name"
              :class="{ 'is-overflow': treeNameOverflow[data.id] }"
              :data-node-id="data.id"
              :title="data.name"
              :style="treeNameOverflow[data.id] ? { '--marquee-distance': treeNameOverflow[data.id] } : null">
              <span class="org-tree-name-text">{{ data.name }}</span>
            </span>
            <el-tag
              v-if="data.tenantType === 1 && data.independentFlag === 1"
              size="mini"
              type="warning"
              effect="plain"
              class="ml-6">独立运营</el-tag>
            <el-tag
              v-if="data.expiryLabel"
              size="mini"
              :type="expiryTagType(data.expiryLabel)"
              effect="plain"
              class="ml-6 expiry-tag">
              {{ data.expiryLabel }}
            </el-tag>
          </span>
        </template>
      </el-tree>
    </div>

    <!-- 右栏：节点属性 -->
    <div class="right bc-white br-4 padding-20 ml-20">
      <div class="display-flex ai-center mb-8">
        <div class="display-flex ai-center selected-node-title">
          <b class="fs-14 selected-node-name">已选择：{{ currentNode ? currentNode.name : '-' }}</b>
          <el-tag v-if="currentNode" size="mini" type="primary" effect="plain" class="selected-node-type">{{ tenantTypeName }}</el-tag>
        </div>
      </div>
      <template v-if="currentNode">
        <div class="display-flex ai-center jc-space-between attr-header">
          <div class="tenant-action-buttons">
            <!-- 集团租户 -->
            <template v-if="currentNode.tenantType === 2">
              <el-button type="text" class="color-success" icon="el-icon-magic-stick" @click="initDialog.show = true">初始化租户</el-button>
              <el-button type="text" icon="el-icon-edit-outline" @click="openEditDialog(currentNode)">编辑集团</el-button>
              <el-button type="text" class="color-danger" icon="el-icon-delete" @click="delNode">删除</el-button>
            </template>
            <!-- 节点租户 -->
            <template v-else-if="currentNode.tenantType === 3">
              <el-button type="text" icon="el-icon-edit-outline" @click="openEditDialog(currentNode)">编辑节点</el-button>
              <el-button type="text" class="color-danger" icon="el-icon-delete" @click="delNode">删除节点</el-button>
            </template>
            <!-- 一般租户 -->
            <template v-else>
              <el-button
                type="text"
                icon="el-icon-sort"
                class="color-warning"
                :title="'切换运营方式：' + (attrForm.independentFlag === 1 ? '切换为集团管理' : '切换为独立运营')"
                @click="openIndependentSwitch">
                {{ attrForm.independentFlag === 1 ? '切换为集团管理' : '切换为独立运营' }}
              </el-button>
              <el-button type="text" class="color-warning" icon="el-icon-right" @click="convertToTenant">转为一般租户</el-button>
              <el-button type="text" icon="el-icon-edit-outline" @click="openEditDialog(currentNode)">编辑租户</el-button>
              <el-button type="text" class="color-danger" icon="el-icon-delete" @click="delNode">删除租户</el-button>
            </template>
            <el-button type="text" class="color-info" icon="el-icon-time" @click="viewHistory">历史操作</el-button>
          </div>
        </div>
        <div v-if="isInheritNode" class="inherit-tip">
          <i class="el-icon-info mr-6"></i>灰色部分字段继承集团根节点配置；消息通知跟随集团，不重复提醒。需要修改时，请前往集团根节点调整。
        </div>
        <!-- 基础属性 -->
        <p class="section-title">基础属性</p>
        <div class="attr-list">
          <div class="attr-item" :class="{ 'attr-disabled': isInheritNode }">
            <span class="attr-label"><i v-if="!isInheritNode" class="req">*</i>有效期</span>
            <span class="fs-14">{{ attrForm.expireDate }}</span>
          </div>
          <div class="attr-item">
            <span class="attr-label"><i class="req">*</i>系统状态</span>
            <el-tag size="mini" :type="statusTagType" effect="plain">{{ statusText }}</el-tag>
          </div>
          <div class="attr-item">
            <span class="attr-label"><i class="req">*</i>超管账号</span>
            <span class="attr-value">
              <span class="fs-14 attr-value-text">{{ attrForm.adminAccount }}</span>
              <i
                v-if="attrForm.adminAccount && attrForm.adminAccount !== '-'"
                class="el-icon-document-copy attr-copy color-primary cursor-pointer"
                title="复制超管账号"
                @click.stop="copyText(attrForm.adminAccount)"></i>
            </span>
          </div>
          <div class="attr-item" :class="{ 'attr-disabled': isInheritNode }">
            <span class="attr-label">消息接收人</span>
            <span class="fs-14">{{ receiverText }}</span>
          </div>
          <div class="attr-item">
            <span class="attr-label">经度</span>
            <span class="fs-14" :class="{ 'color-info': !attrForm.longitude }">{{ attrForm.longitude || '-' }}</span>
          </div>
          <div class="attr-item">
            <span class="attr-label">纬度</span>
            <span class="fs-14" :class="{ 'color-info': !attrForm.latitude }">{{ attrForm.latitude || '-' }}</span>
          </div>
        </div>
        <!-- 运行与资源配置 -->
        <p class="section-title">运行与资源配置</p>
        <div class="attr-list">
          <div class="attr-item" :class="{ 'attr-disabled': isInheritNode }">
            <span class="attr-label"><i class="req">*</i>是否独立部署</span>
            <el-tag size="mini" :type="attrForm.independentDeploy === 1 ? 'warning' : 'info'" effect="plain">
              {{ attrForm.independentDeploy === 1 ? '是' : '否' }}
            </el-tag>
          </div>
          <div class="attr-item" v-if="attrForm.independentDeploy === 1" :class="{ 'attr-disabled': isInheritNode }">
            <span class="attr-label">
              <i class="req">*</i>{{ currentNode && currentNode.tenantType === 2 ? '集团服务域名（询问研发）' : '租户服务域名（询问研发）' }}
            </span>
            <span class="attr-value">
              <span class="fs-14 attr-value-text" :class="{ 'color-info': !attrForm.serviceDomain }">{{ attrForm.serviceDomain || '-' }}</span>
              <i
                v-if="attrForm.serviceDomain"
                class="el-icon-document-copy attr-copy color-primary cursor-pointer"
                title="复制服务域名"
                @click.stop="copyText(attrForm.serviceDomain)"></i>
            </span>
          </div>
          <div class="attr-item" :class="{ 'attr-disabled': isInheritNode }">
            <span class="attr-label"><i v-if="!isInheritNode" class="req">*</i>账户数</span>
            <span class="usage-value" :class="'color-' + usageStatus(attrForm.accountUsed, attrForm.accountTotal)">
              {{ attrForm.accountUsed }}/{{ attrForm.accountTotal }}
            </span>
          </div>
          <div class="attr-item" :class="{ 'attr-disabled': isInheritNode }">
            <span class="attr-label"><i v-if="!isInheritNode" class="req">*</i>短信数</span>
            <span class="usage-value" :class="'color-' + usageStatus(attrForm.smsUsed, attrForm.smsTotal)">
              {{ attrForm.smsUsed }}/{{ attrForm.smsTotal }}
            </span>
          </div>
          <div class="attr-item" :class="{ 'attr-disabled': isInheritNode }">
            <span class="attr-label"><i v-if="!isInheritNode" class="req">*</i>AI识别数</span>
            <span class="usage-value" :class="'color-' + usageStatus(attrForm.aiUsed, attrForm.aiTotal)">
              {{ attrForm.aiUsed }}/{{ attrForm.aiTotal }}
            </span>
          </div>
          <div class="attr-item" :class="{ 'attr-disabled': isInheritNode }">
            <span class="attr-label">更多AI配置</span>
            <span class="fs-14" :class="{ 'color-info': !attrForm.advancedAi }">{{ attrForm.advancedAi ? '培训模块-考试范围支持选择课程' : '-' }}</span>
          </div>
        </div>
      </template>
      <el-empty v-else :description="currentGroup ? '请选择左侧节点' : '请选择一个集团'"></el-empty>
    </div>

    <!-- 添加集团（全字段，传参与一般租户新增一致） -->
    <el-dialog
      :visible.sync="groupDialog.show"
      width="min(800px, calc(100vw - 32px))"
      append-to-body>
      <div slot="title" class="tenant-dialog-titlebar">
        <span>添加集团</span>
        <div class="tenant-quick-config">
          <el-button type="text" size="small" class="quick-config-test" icon="el-icon-magic-stick" @click="applyTestUserPreset">开测试用户</el-button>
          <el-button type="text" size="small" class="quick-config-prod" icon="el-icon-circle-check" @click="applyFormalUserPreset">开正式用户</el-button>
        </div>
      </div>
      <el-form :model="groupDialog.form" label-position="top" class="diy-top-form tenant-form-grid group-add-form">
        <section class="tenant-form-section">
          <b class="edit-section-title">基础属性</b>
          <el-form-item class="geo-action-form-item" label="名称" required>
            <el-input
              v-model="groupDialog.form.name"
              maxlength="50"
              show-word-limit
              placeholder="请输入集团租户名称"
              @input="handleGroupNameInput">
            </el-input>
            <el-button class="geo-action-button" type="text" size="small" icon="el-icon-location-outline" @click="matchGeo('group')">匹配经纬度</el-button>
          </el-form-item>
          <el-form-item label="有效期" required>
            <el-date-picker
              v-model="groupDialog.form.expDate"
              type="date"
              value-format="yyyy-MM-dd"
              placeholder="请选择有效期"
              style="width: 100%">
            </el-date-picker>
          </el-form-item>
          <el-form-item label="系统状态" required>
            <div class="display-flex ai-center" style="height: 40px">
              <el-radio-group v-model="groupDialog.form.enabled">
                <el-radio :label="false">禁用</el-radio>
                <el-radio :label="true">启用</el-radio>
              </el-radio-group>
            </div>
          </el-form-item>
          <el-form-item label="超管账号" required>
            <el-input v-model="groupDialog.form.username" maxlength="50" placeholder="请输入超管账号"></el-input>
          </el-form-item>
          <el-form-item label="消息接收人">
            <limit-select
              v-model="groupDialog.form.msgReceiverIds"
              :options="receiverOptions"
              placeholder="请选择消息接收人（最多 10 人）"
              @change="handleGroupReceiverChange">
            </limit-select>
            <p class="field-hint">此处可选择额外接收消息的人员。</p>
          </el-form-item>
          <div class="display-flex width-percent-100">
            <el-form-item label="经度" style="flex: 1" class="mr-20">
              <el-input v-model="groupDialog.form.longitude" placeholder="请输入经度"></el-input>
            </el-form-item>
            <el-form-item label="纬度" style="flex: 1">
              <el-input v-model="groupDialog.form.latitude" placeholder="请输入纬度"></el-input>
            </el-form-item>
          </div>
        </section>
        <section class="tenant-form-section">
          <b class="edit-section-title">运行与资源配置</b>
          <el-form-item label="是否独立部署" required>
            <div class="display-flex ai-center" style="height: 32px">
              <el-switch v-model="groupDialog.form.independentDeploy" active-color="#E6A23C"></el-switch>
            </div>
          </el-form-item>
          <!-- 独立部署时服务域名为必填项 -->
          <el-form-item v-if="groupDialog.form.independentDeploy" label="集团服务域名（询问研发）" required>
            <el-input
              v-model="groupDialog.form.serviceDomain"
              maxlength="100"
              show-word-limit
              placeholder="请输入服务域名">
            </el-input>
          </el-form-item>
          <el-form-item label="账户数上限" required>
            <el-input-number
              v-model="groupDialog.form.accountSize"
              :min="0"
              :max="100000"
              controls-position="right"
              style="width: 100%">
            </el-input-number>
          </el-form-item>
          <el-form-item label="短信数上限" required>
            <el-input-number
              v-model="groupDialog.form.smsCount"
              :min="0"
              controls-position="right"
              style="width: 100%">
            </el-input-number>
            <p class="field-hint">数量建议为合同金额数除以10。</p>
          </el-form-item>
          <el-form-item label="AI识别数上限" required>
            <el-input-number
              v-model="groupDialog.form.aiCount"
              :min="0"
              controls-position="right"
              style="width: 100%">
            </el-input-number>
            <p class="field-hint">数量建议为合同金额数。</p>
          </el-form-item>
          <el-form-item label="更多AI配置">
            <el-select
              :value="groupDialog.form.advancedAi ? ['培训'] : []"
              multiple
              filterable
              clearable
              placeholder="请选择AI功能"
              style="width: 100%"
              @change="groupDialog.form.advancedAi = $event.includes('培训')">
              <el-option label="培训模块-考试范围支持选择课程" value="培训"></el-option>
            </el-select>
          </el-form-item>
        </section>
      </el-form>
      <span slot="footer">
        <el-button @click="groupDialog.show = false">取 消</el-button>
        <el-button type="primary" :loading="groupDialog.loading" @click="submitGroup">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 新增下级 -->
    <el-dialog
      :title="childDialog.form.target === 'node' ? '新建节点租户' : childDialog.form.source === 'existing' ? '接入已有租户' : '新建一般租户'"
      :visible.sync="childDialog.show"
      :width="childDialog.form.target === 'tenant' && childDialog.form.source === 'new' ? 'min(800px, calc(100vw - 32px))' : '560px'"
      append-to-body>
      <div v-if="childDialog.form.target === 'node'" class="switch-tip mb-16">
        <i class="el-icon-warning-outline mr-6"></i>
        <span>新增节点默认为禁用状态，更多设置请在编辑节点中操作。</span>
      </div>
      <el-form
        :model="childDialog.form"
        ref="childForm"
        label-position="top"
        class="diy-top-form">
        <!-- 一般租户 -->
        <template v-if="childDialog.form.target === 'tenant'">
          <!-- 选择已有一般租户 -->
          <template v-if="childDialog.form.source === 'existing'">
            <el-form-item class="geo-action-form-item" label="选择一般租户" required>
              <div class="display-flex width-percent-100">
                <el-select
                  v-model="childDialog.form.tenantId"
                  style="flex: 1"
                  filterable
                  remote
                  clearable
                  :remote-method="searchUnlinked"
                  :loading="unlinkedLoading"
                  placeholder="请选择未挂接的一般租户">
                  <el-option
                    v-for="item in unlinkedTenants"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id">
                  </el-option>
                </el-select>
              </div>
              <p class="fs-12 color-info margin-0 mt-6">挂接后，资源配置按集团继承，租户基础属性仍可维护。</p>
            </el-form-item>
          </template>
          <template v-if="childDialog.form.source === 'new'">
            <div class="operation-mode-block">
              <b class="dialog-section-title operation-mode-title">运营形式</b>
              <el-form-item class="operation-mode-item">
                <el-radio-group v-model="childDialog.form.independent" size="small">
                  <el-radio-button :label="false">按集团继承</el-radio-button>
                  <el-radio-button :label="true">独立运营</el-radio-button>
                </el-radio-group>
              </el-form-item>
            </div>
          </template>
          <!-- 新建一般租户：基础属性 -->
          <template v-if="childDialog.form.source === 'new'">
            <tenant-create-form
              :form="childDialog.form"
              :receiver-options="receiverOptions"
              :show-expiry="childDialog.form.independent"
              :show-receivers="childDialog.form.independent"
              :show-resource-config="childDialog.form.independent"
              @name-input="handleChildNameInput"
              @receiver-change="handleChildReceiverChange"
              @match-geo="matchGeo('child')">
            </tenant-create-form>
          </template>
        </template>
        <!-- 节点租户 -->
        <template v-else>
          <el-form-item label="节点名称" required>
            <el-input
              v-model="childDialog.form.name"
              maxlength="50"
              show-word-limit
              placeholder="请输入节点名称">
            </el-input>
          </el-form-item>
        </template>
      </el-form>
      <span slot="footer">
        <el-button @click="childDialog.show = false">取 消</el-button>
        <el-button type="primary" :loading="childDialog.loading" @click="submitChild">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 匹配经纬度 -->
    <el-dialog
      title="匹配经纬度"
      :visible.sync="geoDialog.show"
      width="560px"
      append-to-body>
      <p class="fs-14 margin-0 mb-16">根据租户名称匹配到以下候选点位： <b>{{ geoDialog.name }}</b></p>
      <div class="geo-list" v-loading="geoDialog.loading">
        <div
          v-for="(item, index) in geoDialog.list"
          :key="index"
          class="geo-item"
          :class="{ active: geoDialog.selected === index }"
          @click="geoDialog.selected = index">
          <div style="flex: 1">
            <b class="fs-14">{{ item.name }}</b>
            <p class="geo-address margin-0 mt-10">
              <i class="el-icon-location-outline color-primary mr-6"></i>{{ item.address }}
            </p>
            <p class="geo-coord margin-0 mt-6">经度 {{ item.longitude }} · 纬度 {{ item.latitude }}</p>
          </div>
          <el-radio v-model="geoDialog.selected" :label="index"><span></span></el-radio>
        </div>
        <el-empty
          v-if="!geoDialog.loading && !geoDialog.list.length"
          :image-size="80"
          description="未匹配到相关点位，请手动输入经纬度">
        </el-empty>
      </div>
      <span slot="footer">
        <el-button @click="geoDialog.show = false">取 消</el-button>
        <el-button type="primary" :disabled="geoDialog.selected === null" @click="useGeoPoint">使用此点位</el-button>
      </span>
    </el-dialog>

    <!-- 补充上级 -->
    <el-dialog
      title="补充上级"
      :visible.sync="parentDialog.show"
      width="480px"
      append-to-body>
      <el-form :model="parentDialog.form" ref="parentForm" label-position="top" class="diy-top-form">
        <el-form-item
          label="上级名称"
          prop="name"
          :rules="{ required: true, message: '请输入上级节点名称', trigger: 'blur' }">
          <el-input
            v-model="parentDialog.form.name"
            maxlength="50"
            show-word-limit
            placeholder="请输入上级节点名称">
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="parentDialog.show = false">取 消</el-button>
        <el-button type="primary" :loading="parentDialog.loading" @click="submitParent">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 切换运营状态（集团管理 / 独立运营） -->
    <el-dialog
      :title="(currentNode ? currentNode.name : '') + (switchDialog.target === 1 ? '切换为独立运营' : '切换为集团管理')"
      :visible.sync="switchDialog.show"
      width="min(800px, calc(100vw - 32px))"
      append-to-body
      @close="cancelSwitch">
      <div class="switch-tip mb-16">
        <i class="el-icon-warning-outline mr-6"></i>
        <span v-if="switchDialog.target === 1">以下属性读取该租户历史独立配置；若无历史独立配置请手动填写。</span>
        <span v-else>租户当前独立配置将保留但不生效；以后若改为独立运营时可继续参考该配置。</span>
      </div>
      <el-form :model="switchDialog.form" label-position="top" class="diy-top-form tenant-form-grid switch-tenant-form">
        <template v-if="switchDialog.target === 1">
          <section class="tenant-form-section">
            <b class="edit-section-title">独立运营配置</b>
            <el-form-item label="有效期" required>
              <el-date-picker
                v-model="switchDialog.form.expireDate"
                type="date"
                value-format="yyyy-MM-dd"
                :clearable="false"
                placeholder="请选择有效期"
                style="width: 100%">
              </el-date-picker>
            </el-form-item>
            <el-form-item label="系统状态" required>
              <div class="display-flex ai-center" style="height: 40px">
                <el-radio-group v-model="switchDialog.form.enabled">
                  <el-radio :label="false">禁用</el-radio>
                  <el-radio :label="true">启用</el-radio>
                </el-radio-group>
              </div>
            </el-form-item>
            <el-form-item label="超管账号" required>
              <el-input v-model="switchDialog.form.username" maxlength="50" placeholder="请输入超管账号"></el-input>
            </el-form-item>
            <el-form-item label="消息接收人">
              <limit-select
                v-model="switchDialog.form.notifyUserIds"
                :options="receiverOptions"
                placeholder="请选择消息接收人（最多 10 人）"
                @change="handleReceiverChange($event, switchDialog.form)">
              </limit-select>
              <p class="field-hint">此处可选择额外接收消息的人员。</p>
            </el-form-item>
            <div class="display-flex width-percent-100">
              <el-form-item label="经度" style="flex: 1" class="mr-20">
                <el-input v-model="switchDialog.form.longitude" maxlength="20" placeholder="请输入经度"></el-input>
              </el-form-item>
              <el-form-item label="纬度" style="flex: 1">
                <el-input v-model="switchDialog.form.latitude" maxlength="20" placeholder="请输入纬度"></el-input>
              </el-form-item>
            </div>
          </section>
          <section class="tenant-form-section">
            <b class="edit-section-title">运行与资源配置</b>
            <el-form-item label="是否独立部署" required>
              <div class="display-flex ai-center" style="height: 32px">
                <el-switch v-model="switchDialog.form.independentDeploy" active-color="#E6A23C"></el-switch>
              </div>
            </el-form-item>
            <el-form-item v-if="switchDialog.form.independentDeploy" label="租户服务域名" required>
              <el-input
                v-model="switchDialog.form.serviceDomain"
                maxlength="100"
                show-word-limit
                placeholder="请输入服务域名">
              </el-input>
            </el-form-item>
            <el-form-item label="账户数上限" required>
              <el-input-number v-model="switchDialog.form.accountSize" :min="0" :max="100000" controls-position="right" style="width: 100%"></el-input-number>
            </el-form-item>
            <el-form-item label="短信数上限" required>
              <el-input-number v-model="switchDialog.form.smsCount" :min="0" controls-position="right" style="width: 100%"></el-input-number>
              <p class="field-hint">数量建议为合同金额数除以10。</p>
            </el-form-item>
            <el-form-item label="AI识别数上限" required>
              <el-input-number v-model="switchDialog.form.aiCount" :min="0" controls-position="right" style="width: 100%"></el-input-number>
              <p class="field-hint">数量建议为合同金额数。</p>
            </el-form-item>
            <el-form-item label="更多AI配置">
              <el-select
                :value="switchDialog.form.openAi ? ['培训'] : []"
                multiple
                filterable
                clearable
                placeholder="请选择AI功能"
                style="width: 100%"
                @change="switchDialog.form.openAi = $event.includes('培训')">
                <el-option label="培训模块-考试范围支持选择课程" value="培训"></el-option>
              </el-select>
            </el-form-item>
          </section>
        </template>
        <section class="tenant-form-section switch-reason-section">
          <b class="edit-section-title">切换说明</b>
          <el-form-item label="操作原因">
            <el-input
              v-model="switchDialog.form.reason"
              type="textarea"
              :rows="3"
              placeholder="请输入操作原因">
            </el-input>
          </el-form-item>
        </section>
      </el-form>
      <span slot="footer">
        <el-button @click="switchDialog.show = false">取 消</el-button>
        <el-button type="primary" :loading="switchDialog.loading" @click="submitSwitch">确认切换</el-button>
      </span>
    </el-dialog>

    <!-- 整体编辑（集团/节点/租户共用；集团租户与独立运营租户可编辑全部，其余仅基础属性） -->
    <el-dialog
      :title="editDialogTitle"
      :visible.sync="editDialog.show"
      width="min(800px, calc(100vw - 32px))"
      append-to-body>
      <el-form label-position="top" class="diy-top-form tenant-form-grid group-edit-form">
        <section class="tenant-form-section">
          <b class="edit-section-title">基础属性</b>
          <el-form-item class="geo-action-form-item" label="名称" required>
            <el-input
              v-model="editDialog.form.name"
              maxlength="50"
              show-word-limit
              placeholder="请输入名称">
            </el-input>
            <el-button class="geo-action-button" type="text" size="small" icon="el-icon-location-outline" @click="matchGeo('edit')">匹配经纬度</el-button>
          </el-form-item>
          <el-form-item label="有效期" :required="editIsFull">
            <el-date-picker
              v-model="editDialog.form.expireDate"
              type="date"
              value-format="yyyy-MM-dd"
              placeholder="跟随集团"
              style="width: 100%"
              :disabled="!editIsFull">
            </el-date-picker>
          </el-form-item>
          <el-form-item label="系统状态" required>
            <!-- 集团/节点租户：下拉选择，直接对应 state 1/3/4 -->
            <el-select
              v-if="editDialog.node && editDialog.node.tenantType !== 1"
              v-model="editDialog.form.state"
              style="width: 100%">
              <el-option label="启用" :value="1"></el-option>
              <el-option label="仅禁用该节点" :value="3">
                <span class="color-warning">仅禁用该节点</span>
              </el-option>
              <el-option label="全部禁用" :value="4">
                <span class="color-danger">全部禁用</span>
              </el-option>
            </el-select>
            <!-- 下属租户：使用单选项设置启用/禁用 -->
            <div v-else class="display-flex ai-center" style="height: 40px">
              <el-radio-group v-model="editDialog.form.enabled">
                <el-radio :label="false">禁用</el-radio>
                <el-radio :label="true">启用</el-radio>
              </el-radio-group>
            </div>
          </el-form-item>
          <el-form-item label="超管账号" required>
            <el-input
              v-model="editDialog.form.adminAccount"
              maxlength="50"
              placeholder="请输入超管账号">
            </el-input>
          </el-form-item>
          <el-form-item>
            <span slot="label">消息接收人</span>
            <!-- 集团租户/独立运营租户：可选择额外接收消息的人员 -->
            <limit-select
              v-if="editIsFull"
              v-model="editDialog.form.notifyUserIds"
              :options="receiverOptions"
              placeholder="请选择额外接收消息的人员（最多 10 人）"
              @change="handleReceiverChange">
            </limit-select>
            <p v-if="editIsFull" class="field-hint">此处可选择额外接收消息的人员。</p>
            <!-- 节点租户/集团管理租户：仅展示，消息通知跟随集团 -->
            <p v-else class="fs-14 color-info margin-0">{{ editInheritReceiverText }}</p>
          </el-form-item>
          <div class="display-flex width-percent-100">
            <el-form-item label="经度" style="flex: 1" class="mr-20">
              <el-input v-model="editDialog.form.longitude" placeholder="请输入经度"></el-input>
            </el-form-item>
            <el-form-item label="纬度" style="flex: 1">
              <el-input v-model="editDialog.form.latitude" placeholder="请输入纬度"></el-input>
            </el-form-item>
          </div>
        </section>
        <section class="tenant-form-section">
          <b class="edit-section-title">运行与资源配置</b>
          <el-form-item label="是否独立部署" required>
            <div class="display-flex ai-center" style="height: 32px">
              <el-switch
                v-model="editDialog.form.independentDeploy"
                :disabled="!editIsFull"
                active-color="#E6A23C">
              </el-switch>
            </div>
          </el-form-item>
          <!-- 独立部署时才需要填写服务域名 -->
          <el-form-item
            v-if="editDialog.form.independentDeploy"
            :label="editDialog.node && editDialog.node.tenantType === 2 ? '集团服务域名（询问研发）' : '租户服务域名（询问研发）'"
            required>
            <el-input
              v-model="editDialog.form.serviceDomain"
              maxlength="100"
              show-word-limit
              placeholder="请输入服务域名"
              :disabled="!editIsFull">
            </el-input>
          </el-form-item>
          <el-form-item label="账户数上限" :required="editIsFull" style="width: 100%">
            <el-input-number
              v-model="editDialog.form.accountTotal"
              :min="0"
              :max="100000"
              controls-position="right"
              style="width: 100%"
              :disabled="!editIsFull">
            </el-input-number>
          </el-form-item>
          <el-form-item label="短信数上限" :required="editIsFull" style="width: 100%">
            <el-input-number
              v-model="editDialog.form.smsTotal"
              :min="0"
              controls-position="right"
              style="width: 100%"
              :disabled="!editIsFull">
            </el-input-number>
            <p class="field-hint">数量建议为合同金额数除以10。</p>
          </el-form-item>
          <el-form-item label="AI识别数上限" :required="editIsFull" style="width: 100%">
            <el-input-number
              v-model="editDialog.form.aiTotal"
              :min="0"
              controls-position="right"
              style="width: 100%"
              :disabled="!editIsFull">
            </el-input-number>
            <p class="field-hint">数量建议为合同金额数。</p>
          </el-form-item>
          <el-form-item label="更多AI配置">
            <el-select
              :value="editDialog.form.advancedAi ? ['培训'] : []"
              multiple
              filterable
              clearable
              placeholder="请选择AI功能"
              style="width: 100%"
              :disabled="!editIsFull"
              @change="editDialog.form.advancedAi = $event.includes('培训')">
              <el-option label="培训模块-考试范围支持选择课程" value="培训"></el-option>
            </el-select>
          </el-form-item>
        </section>
      </el-form>
      <span slot="footer">
        <el-button @click="editDialog.show = false">取 消</el-button>
        <el-button type="primary" :loading="editDialog.loading" @click="submitEdit">保 存</el-button>
      </span>
    </el-dialog>

    <!-- 调整所属节点 -->
    <el-dialog
      title="调整所属节点"
      :visible.sync="moveDialog.show"
      width="480px"
      append-to-body>
      <p class="fs-14 margin-0 mb-16">请选择「{{ currentNode && currentNode.name }}」要挂载到的上级节点。</p>
      <div class="move-tree" v-loading="moveDialog.treeLoading">
        <el-tree
          :data="moveDialog.tree"
          node-key="id"
          highlight-current
          :expand-on-click-node="false"
          default-expand-all
          :props="{ label: 'name', children: 'children' }"
          @node-click="handleMoveSelect">
          <span slot-scope="{ data }" class="display-flex ai-center">
            <i class="iconfont icon-folder color-primary mr-6"></i>
            <span>{{ data.name }}</span>
          </span>
        </el-tree>
      </div>
      <span slot="footer">
        <el-button @click="moveDialog.show = false">取 消</el-button>
        <el-button type="primary" :loading="moveDialog.loading" :disabled="!moveDialog.targetId" @click="submitMove">确认调整</el-button>
      </span>
    </el-dialog>

    <!-- 转为一般租户 -->
    <el-dialog
      :title="(currentNode ? currentNode.name : '') + '转为一般租户'"
      :visible.sync="convertDialog.show"
      width="min(800px, calc(100vw - 32px))"
      append-to-body>
      <div class="switch-tip mb-16">
        <i class="el-icon-warning-outline mr-6"></i>
        <span>转为一般租户后将脱离集团，请重新确认租户属性。</span>
      </div>
      <el-form :model="convertDialog.form" label-position="top" class="diy-top-form tenant-form-grid convert-tenant-form">
        <section class="tenant-form-section">
          <b class="edit-section-title">基础属性</b>
          <el-form-item label="有效期" required>
            <el-date-picker
              v-model="convertDialog.form.expireDate"
              type="date"
              value-format="yyyy-MM-dd"
              :clearable="false"
              placeholder="请选择有效期"
              style="width: 100%">
            </el-date-picker>
          </el-form-item>
          <el-form-item label="系统状态" required>
            <div class="display-flex ai-center" style="height: 40px">
              <el-radio-group v-model="convertDialog.form.enabled">
                <el-radio :label="false">禁用</el-radio>
                <el-radio :label="true">启用</el-radio>
              </el-radio-group>
            </div>
          </el-form-item>
          <el-form-item label="超管账号" required>
            <el-input v-model="convertDialog.form.username" maxlength="50" placeholder="请输入超管账号"></el-input>
          </el-form-item>
          <el-form-item label="消息接收人">
            <limit-select
              v-model="convertDialog.form.notifyUserIds"
              :options="receiverOptions"
              placeholder="请选择消息接收人（最多 10 人）"
              @change="handleReceiverChange($event, convertDialog.form)">
            </limit-select>
            <p class="field-hint">此处可选择额外接收消息的人员。</p>
          </el-form-item>
          <div class="display-flex width-percent-100">
            <el-form-item label="经度" style="flex: 1" class="mr-20">
              <el-input v-model="convertDialog.form.longitude" maxlength="20" placeholder="请输入经度"></el-input>
            </el-form-item>
            <el-form-item label="纬度" style="flex: 1">
              <el-input v-model="convertDialog.form.latitude" maxlength="20" placeholder="请输入纬度"></el-input>
            </el-form-item>
          </div>
        </section>
        <section class="tenant-form-section">
          <b class="edit-section-title">运行与资源配置</b>
          <el-form-item label="账户数上限" required>
            <el-input-number v-model="convertDialog.form.accountSize" :min="0" :max="100000" controls-position="right" style="width: 100%"></el-input-number>
          </el-form-item>
          <el-form-item label="短信数上限" required>
            <el-input-number v-model="convertDialog.form.smsCount" :min="0" controls-position="right" style="width: 100%"></el-input-number>
            <p class="field-hint">数量建议为合同金额数除以10。</p>
          </el-form-item>
          <el-form-item label="AI识别数上限" required>
            <el-input-number v-model="convertDialog.form.aiCount" :min="0" controls-position="right" style="width: 100%"></el-input-number>
            <p class="field-hint">数量建议为合同金额数。</p>
          </el-form-item>
          <el-form-item label="更多AI配置">
            <el-select
              :value="convertDialog.form.openAi ? ['培训'] : []"
              multiple
              filterable
              clearable
              placeholder="请选择AI功能"
              style="width: 100%"
              @change="convertDialog.form.openAi = $event.includes('培训')">
              <el-option label="培训模块-考试范围支持选择课程" value="培训"></el-option>
            </el-select>
          </el-form-item>
        </section>
        <section class="tenant-form-section convert-reason-section">
          <b class="edit-section-title">转换说明</b>
          <el-form-item label="操作原因" required>
            <el-input
              v-model="convertDialog.form.reason"
              type="textarea"
              :rows="3"
              maxlength="200"
              show-word-limit
              placeholder="请输入操作原因">
            </el-input>
          </el-form-item>
        </section>
      </el-form>
      <span slot="footer">
        <el-button @click="convertDialog.show = false">取 消</el-button>
        <el-button type="primary" :loading="convertDialog.loading" @click="submitConvert">确认转换</el-button>
      </span>
    </el-dialog>

    <!-- 查看操作历史 -->
    <el-dialog
      :title="(currentNode ? currentNode.name : '') + '租户的操作历史'"
      :visible.sync="historyDialog.show"
      width="860px"
      append-to-body
      @opened="$refs.historyTable && $refs.historyTable.doLayout()">
      <el-table ref="historyTable" :data="historyDialog.list" v-loading="historyDialog.loading" border size="small" max-height="400">
        <el-table-column prop="createAt" label="操作时间" width="150"></el-table-column>
        <el-table-column prop="operation" label="操作" width="110" show-overflow-tooltip></el-table-column>
        <el-table-column prop="objectPath" label="操作对象" width="160" show-overflow-tooltip></el-table-column>
        <el-table-column prop="reason" label="操作原因" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column prop="createByName" label="操作人" width="90"></el-table-column>
        <template slot="empty">暂无操作历史</template>
      </el-table>
      <div class="display-flex jc-center mt-16">
        <el-pagination
          hide-on-single-page
          @current-change="handleHistoryPageChange"
          :current-page="historyDialog.pageNum"
          :page-size="historyDialog.pageSize"
          layout="prev, pager, next"
          :total="historyDialog.total">
        </el-pagination>
      </div>
      <span slot="footer">
        <el-button @click="historyDialog.show = false">关 闭</el-button>
      </span>
    </el-dialog>

    <!-- 初始化租户 -->
    <init-tenant-dialog
      :visible.sync="initDialog.show"
      :tenant-id="currentNode ? currentNode.id : null"
      :tenant-name="currentNode ? currentNode.name : ''">
    </init-tenant-dialog>

    <!-- 导入组织架构（POST /tenant/group/importOrgTree） -->
    <el-dialog
      title="导入组织架构"
      :visible.sync="importDialog.show"
      width="560px"
      append-to-body
      @closed="resetImportDialog">
      <div class="display-flex ai-center import-tip">
        <i class="el-icon-info"></i>
        <span>请先下载模板，按模板填写后再上传文件。</span>
      </div>
      <div class="display-flex ai-center jc-space-between mt-16 mb-10">
        <span class="fs-14">集团组织架构导入模板</span>
        <el-button type="text" icon="el-icon-download" @click="downloadOrgTemplate">下载模板</el-button>
      </div>
      <el-upload
        drag
        action=""
        class="import-upload"
        :auto-upload="false"
        :show-file-list="false"
        accept=".xlsx"
        :on-change="handleImportFileChange">
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将填写完成的模板拖入此处，或点击上传</div>
        <div class="el-upload__tip">仅支持 .xlsx 文件，单次上传 1 个，文件不超过 10MB</div>
      </el-upload>
      <div v-if="importDialog.fileName" class="import-file mt-10">
        <i class="el-icon-document color-primary"></i>
        <span class="ml-6 text-ellipsis">{{ importDialog.fileName }}</span>
        <el-button type="text" class="ml-10" @click="resetImportDialog">重新上传</el-button>
      </div>
      <span slot="footer">
        <el-button @click="importDialog.show = false">取 消</el-button>
        <el-button type="primary" :loading="importDialog.loading" @click="submitImport">确认导入</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import _ from 'lodash';
import InitTenantDialog from './InitTenantDialog.vue';
import LimitSelect from '@/components/LimitSelect.vue';
import TenantCreateForm from '@/components/TenantCreateForm.vue';

export default {
  name: 'GroupTenant', // 集团租户
  components: { InitTenantDialog, LimitSelect, TenantCreateForm },
  props: {},
  data () {
    return {
      // 左栏：集团列表
      groupSearch: '',
      groupPage: 1,
      groupTotal: 0,
      groupList: [],
      currentGroup: null,
      // 中栏：组织树
      treeFilter: '',
      treeKey: 0,
      treeNameOverflow: {},
      childTypePopoverVisible: false,
      expandedKeys: [],
      treeProps: {
        children: 'children',
        label: 'name'
      },
      treeData: [],
      // 右栏：节点属性
      currentNode: null,
      attrForm: {
        status: 1,
        state: 1, // 接口返回的原始状态（1启用 2禁用 3仅禁用此节点 4全部禁用）
        independentFlag: 0,
        adminAccount: '-',
        // TODO: 消息接收人字段以后端最终返回为准
        notifyReceivers: [],
        notifyUsers: [],
        notifyUserIds: [],
        longitude: '',
        latitude: '',
        expireDate: '-',
        // TODO: 独立部署标识/租户服务域名接口暂未提供
        independentDeploy: 0,
        serviceDomain: '',
        accountUsed: 0,
        accountTotal: 0,
        aiUsed: 0,
        aiTotal: 0,
        advancedAi: false,
        smsUsed: 0,
        smsTotal: 0
      },
      // 弹框
      groupDialog: {
        show: false,
        loading: false,
        form: {
          name: '',
          expDate: '',
          enabled: true,
          username: '',
          msgReceiverIds: [],
          longitude: '',
          latitude: '',
          independentDeploy: true,
          serviceDomain: '',
          accountSize: null,
          aiCount: null,
          advancedAi: false,
          smsCount: null
        }
      },
      childDialog: {
        show: false,
        loading: false,
        form: {
          target: 'tenant', // tenant 一般租户 / node 节点租户
          source: 'new', // existing 选择已有 / new 新建
          tenantId: null,
          name: '',
          independent: false,
          enabled: true,
          username: '',
          longitude: '',
          latitude: '',
          // 独立运营时填写
          expDate: '',
          msgReceiverIds: [],
          independentDeploy: false,
          serviceDomain: '',
          openAiList: [],
          accountSize: 10,
          aiCount: 1000,
          smsCount: 100
        }
      },
      // 未挂接的一般租户
      unlinkedTenants: [],
      unlinkedLoading: false,
      // 匹配经纬度弹框
      geoDialog: {
        show: false,
        loading: false,
        name: '',
        selected: null,
        list: []
      },
      parentDialog: {
        show: false,
        loading: false,
        form: { name: '' }
      },
      // 切换运营状态弹框
      switchDialog: {
        show: false,
        loading: false,
        confirmed: false,
        target: 1, // 0集团管理 1独立运营
        form: {
          reason: '',
          enabled: true,
          originState: 1,
          username: '',
          notifyUserIds: [],
          expireDate: '',
          longitude: '',
          latitude: '',
          independentDeploy: false,
          serviceDomain: '',
          accountSize: null,
          aiCount: null,
          smsCount: null,
          openAi: false
        }
      },
      // 整体编辑弹框（集团/节点/租户共用）
      editDialog: {
        show: false,
        loading: false,
        node: null, // { id, name, tenantType, independentFlag }
        form: {
          name: '',
          enabled: true,
          adminAccount: '',
          notifyUserIds: [],
          longitude: '',
          latitude: '',
          expireDate: '',
          independentDeploy: false,
          serviceDomain: '',
          accountTotal: null,
          aiTotal: null,
          advancedAi: false,
          smsTotal: null
        }
      },
      // 消息接收人候选人员（/sys/user/options，只加载一次）
      receiverOptions: [],
      receiversLoaded: false,
      // 调整所属节点弹框
      moveDialog: {
        show: false,
        loading: false,
        treeLoading: false,
        targetId: null,
        tree: []
      },
      // 转为一般租户弹框
      convertDialog: {
        show: false,
        loading: false,
        form: {
          reason: '',
          enabled: true,
          originState: 1,
          username: '',
          notifyUserIds: [],
          expireDate: '',
          accountSize: null,
          aiCount: null,
          smsCount: null,
          openAi: false,
          longitude: '',
          latitude: ''
        }
      },
      // 操作历史弹框
      historyDialog: {
        show: false,
        loading: false,
        list: [],
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      // 匹配经纬度回填目标：child 新增下级 / edit 整体编辑
      geoTarget: 'child',
      // 初始化租户弹框（接口暂未接入，仅 UI 占位）
      initDialog: {
        show: false
      },
      // 导入组织架构弹框（POST /tenant/group/importOrgTree）
      importDialog: {
        show: false,
        loading: false,
        file: null, // 已选择的 xlsx 原始文件
        fileName: ''
      }
    }
  },
  filters: {},
  computed: {
    maxTreeDepth () {
      const getDepth = nodes => nodes.reduce((depth, node) => {
        return Math.max(depth, 1 + getDepth(node.children || []))
      }, 0)
      return getDepth(this.treeData)
    },
    middleWidth () {
      const extraLevels = Math.max(0, this.maxTreeDepth - 3)
      return `calc(28% + ${extraLevels * 20}px)`
    },
    /**
     * 当前树根节点id（根节点没有上级，可补充上级）
     */
    rootNodeId () {
      return this.treeData.length ? this.treeData[0].id : null
    },
    /**
     * 租户类型名称：1下属租户 2集团租户 3节点租户
     */
    tenantTypeName () {
      if (!this.currentNode) return ''
      const map = { 1: '下属租户', 2: '集团租户', 3: '节点租户' }
      return map[this.currentNode.tenantType] || ''
    },
    /**
     * 当前节点是否继承集团配置（节点租户 / 集团管理的下属租户）
     */
    isInheritNode () {
      if (!this.currentNode) return false
      return this.currentNode.tenantType === 3 ||
        (this.currentNode.tenantType === 1 && this.currentNode.independentFlag !== 1)
    },
    /**
     * 启用状态展示
     */
    statusText () {
      return { 1: '已启用', 3: '仅禁用此节点', 4: '全部禁用' }[this.attrForm.status] || '已启用'
    },
    statusTagType () {
      return { 1: 'success', 3: 'warning', 4: 'danger' }[this.attrForm.status] || 'success'
    },
    /**
     * 消息接收人展示：继承节点显示"继承集团设置"，其余显示人员姓名
     * TODO: 消息接收人接口暂未提供，暂无数据时按占位文案展示
     */
    receiverText () {
      const users = this.attrForm.notifyUsers || []
      if (!this.isInheritNode) {
        return users.length ? users.join('、') : '-'
      }
      if (users.length) {
        const shown = users.slice(0, 3).join('、')
        return `继承集团设置（${shown}${users.length > 3 ? `等 ${users.length} 人` : ''}）`
      }
      return '跟随集团设置'
    },
    /**
     * 编辑弹框标题
     */
    editDialogTitle () {
      const node = this.editDialog.node
      if (!node) return ''
      if (node.tenantType === 2) return '编辑集团租户'
      if (node.tenantType === 3) return '编辑节点租户'
      return '编辑租户'
    },
    /**
     * 编辑弹框是否可整体编辑（集团租户 / 独立运营的下属租户）
     * 节点租户与集团管理的下属租户只能修改基础属性
     */
    editIsFull () {
      const node = this.editDialog.node
      return !!node && (node.tenantType === 2 || (node.tenantType === 1 && node.independentFlag === 1))
    },
    /**
     * 编辑弹框中消息接收人的继承展示文案（节点租户/集团管理租户为纯展示）
     */
    editInheritReceiverText () {
      const users = this.attrForm.notifyUsers || []
      if (users.length) {
        const shown = users.slice(0, 3).join('、')
        return `跟随集团（${shown}${users.length > 3 ? `等 ${users.length} 人` : ''}）`
      }
      return '跟随集团（消息通知跟随集团，不重复提醒）'
    }
  },
  watch: {
    treeFilter () {
      this.handleTreeSearch()
    },
    groupSearch () {
      this.groupPage = 1
      this.getGroupList()
    }
  },
  methods: {
    /**
     * 到期提示徽标颜色："N天后过期"为提醒色，其余（今天过期/过期N天/长期过期）为危险色
     */
    expiryTagType (label) {
      if (!label) return 'info'
      return label.includes('天后过期') ? 'warning' : 'danger'
    },
    usageStatus (used, total) {
      const totalCount = Number(total)
      const rate = Number(used) / totalCount
      if (!Number.isFinite(rate)) return 'danger'
      if (rate < 0.5) return 'success'
      return rate < 0.8 ? 'warning' : 'danger'
    },
    copyText (text) {
      this.$copyText(text).then(() => {
        this.$message.success('已复制')
      }).catch(() => {
        this.$message.error('复制失败')
      })
    },
    /**
     * 获取集团分页列表
     */
    getGroupList: _.debounce(function () {
      const data = {
        pageNum: this.groupPage,
        pageSize: 10,
        fuzzy: this.groupSearch
      }
      this.$api.tenantGroup.getGroupList(data).then(res => {
        this.groupList = res.list || []
        this.groupTotal = res.total || 0
        if (this.currentGroup && !this.groupList.some(item => item.id === this.currentGroup.id)) {
          this.currentGroup = null
          this.currentNode = null
          this.treeData = []
        }
        if (this.groupList.length && !this.currentGroup) {
          this.changeGroup(this.groupList[0])
        }
      })
    }, 500),
    /**
     * 集团列表翻页
     */
    handleGroupPageChange (val) {
      this.groupPage = val
      this.getGroupList()
    },
    /**
     * 获取集团组织树
     */
    loadGroupTree (selectRoot = false) {
      if (!this.currentGroup) {
        this.treeData = []
        return
      }
      this.$api.tenantGroup.getGroupTree({
        groupId: this.currentGroup.id,
        fuzzy: this.treeFilter
      }).then(res => {
        const toTreeNode = node => {
          const children = node.children || []
          return {
            ...node,
            _hasChildren: children.length > 0,
            children: node.tenantType === 1 ? [] : children.map(toTreeNode)
          }
        }
        this.treeData = res ? [toTreeNode(res)] : []
        // 按后端 spread 标记展开节点
        const expandedKeys = []
        const collect = (nodes) => {
          nodes.forEach(node => {
            if (node.spread) expandedKeys.push(node.id)
            if (node.children && node.children.length) collect(node.children)
          })
        }
        collect(this.treeData)
        const selectedNodeId = this.currentNode && this.currentNode.id
        let selectedNode = null
        const findSelectedNode = (nodes, parentIds = []) => {
          for (const node of nodes) {
            if (node.id === selectedNodeId) {
              selectedNode = node
              expandedKeys.push(...parentIds)
              return true
            }
            if (node.children && findSelectedNode(node.children, [...parentIds, node.id])) return true
          }
          return false
        }
        if (selectedNodeId) findSelectedNode(this.treeData)
        this.expandedKeys = [...new Set(expandedKeys)]
        this.treeKey++ // 强制重新渲染树，应用展开状态
        this.$nextTick(() => {
          this.measureTreeNameOverflow()
          if ((selectRoot && this.treeData.length) || selectedNode) {
            const node = selectRoot ? this.treeData[0] : selectedNode
            this.$refs.orgTree.setCurrentKey(node.id)
            this.handleNodeClick(node)
          }
        })
      })
    },
    handleTreeExpand () {
      this.$nextTick(this.measureTreeNameOverflow)
    },
    measureTreeNameOverflow () {
      const overflow = {}
      const nameElements = this.$el.querySelectorAll('.org-tree-name')
      nameElements.forEach(element => {
        const text = element.querySelector('.org-tree-name-text')
        const distance = text ? Math.ceil(text.scrollWidth - element.clientWidth) : 0
        if (distance > 0) overflow[element.dataset.nodeId] = `-${distance}px`
      })
      this.treeNameOverflow = overflow
    },
    /**
     * 树搜索（服务端过滤，防抖）
     */
    handleTreeSearch: _.debounce(function () {
      this.loadGroupTree()
    }, 500),
    /**
     * 打开导入组织架构弹框
     */
    openImportDialog () {
      if (!this.currentGroup) {
        this.$message.warning('请先选择集团')
        return
      }
      this.importDialog.show = true
    },
    /**
     * 重置导入弹框（清空已选文件）
     */
    resetImportDialog () {
      this.importDialog.loading = false
      this.importDialog.file = null
      this.importDialog.fileName = ''
    },
    /**
     * 下载集团组织架构导入模板（public/templates 下静态文件）
     */
    downloadOrgTemplate () {
      const a = document.createElement('a')
      a.href = encodeURI(`${process.env.BASE_URL}templates/集团组织架构导入模板.xlsx`)
      a.download = '集团组织架构导入模板.xlsx'
      a.click()
    },
    /**
     * 选择导入文件：仅支持 .xlsx，单次上传 1 个，文件不超过 10MB
     */
    handleImportFileChange (file) {
      const raw = file.raw
      if (!raw) return
      if (!/\.xlsx$/i.test(raw.name)) {
        this.$message.warning('仅支持 .xlsx 文件')
        return
      }
      if (raw.size > 10 * 1024 * 1024) {
        this.$message.warning('文件不能超过 10MB')
        return
      }
      this.importDialog.file = raw
      this.importDialog.fileName = raw.name
    },
    /**
     * 确认导入：multipart/form-data 上传 xlsx，groupId 走 query，成功后刷新组织树
     */
    submitImport () {
      if (!this.currentGroup) {
        this.$message.warning('请先选择集团')
        return
      }
      if (!this.importDialog.file) {
        this.$message.warning('请先上传填写完成的模板文件')
        return
      }
      const data = new FormData()
      data.append('file', this.importDialog.file)
      this.importDialog.loading = true
      this.$api.tenantGroup.importOrgTree(data, { groupId: this.currentGroup.id }).then(() => {
        this.importDialog.loading = false
        this.$message.success('导入成功')
        this.importDialog.show = false
        this.loadGroupTree()
      }).catch(() => {
        this.importDialog.loading = false
      })
    },
    /**
     * 切换集团
     */
    changeGroup (item) {
      this.currentGroup = item
      this.currentNode = null
      this.loadGroupTree(true)
    },
    /**
     * 点击树节点：加载节点产品信息
     */
    handleNodeClick (data) {
      this.currentNode = data
      this.attrForm.independentFlag = data.independentFlag || 0
      if (data.accountUsed !== undefined && data.accountUsed !== null) {
        this.attrForm.accountUsed = data.accountUsed
      }
      this.getProductInfo(data.id)
    },
    /**
     * 查询节点/租户产品信息
     */
    getProductInfo (tenantId, cb) {
      this.$api.tenantGroup.getProduct({ tenantId }).then(res => {
        this.fillAttrForm(res)
        if (cb) cb()
      })
    },
    /**
     * 产品信息回填到属性面板
     */
    fillAttrForm (info) {
      info = info || {}
      // 接口返回的原始状态：1启用 2禁用 3仅禁用此节点 4全部禁用（编辑时据此回显并原样回传）
      this.attrForm.state = info.state || 1
      // state 启用状态：1启用 2禁用 3仅禁用此节点 4全部禁用（2 按仅禁用此节点展示）
      this.attrForm.status = info.state === 2 ? 3 : (info.state || 1)
      this.attrForm.adminAccount = info.username || '-'
      this.attrForm.longitude = info.longitude || ''
      this.attrForm.latitude = info.latitude || ''
      this.attrForm.expireDate = info.expDate || '-'
      this.attrForm.accountTotal = info.accountSize || 0
      this.attrForm.aiUsed = info.userAiCount || 0
      this.attrForm.aiTotal = info.aiCount || 0
      this.attrForm.smsUsed = info.smsUseCount || 0
      this.attrForm.smsTotal = info.smsCount || 0
      this.attrForm.independentDeploy = info.independentDeployFlag === 1 ? 1 : 0
      this.attrForm.serviceDomain = info.serviceDomain || ''
      const receivers = Array.isArray(info.msgReceiverList) ? info.msgReceiverList : []
      this.attrForm.notifyReceivers = receivers
      this.attrForm.notifyUsers = receivers.map(r => r.name || r.username).filter(Boolean)
      // 优先取 msgReceiverList 里的 id；没有明细列表时兜底用 msgReceivers（id 数组或逗号分隔字符串）
      let receiverIds = receivers.map(r => r.id || r.userId).filter(id => id !== null && id !== undefined)
      if (!receiverIds.length && info.msgReceivers) {
        receiverIds = Array.isArray(info.msgReceivers)
          ? info.msgReceivers
          : String(info.msgReceivers).split(',').filter(Boolean)
      }
      this.attrForm.notifyUserIds = receiverIds
      // openAi 为已开通的高级AI类型数组，如 '[0]' 表示开通了培训，空字符串表示未开通
      try {
        const openAi = JSON.parse(info.openAi || '[]')
        this.attrForm.advancedAi = Array.isArray(openAi) && openAi.includes(0)
      } catch (e) {
        this.attrForm.advancedAi = false
      }
    },
    /**
     * 名称联动超管账号：添加集团时名称变化超管账号始终跟随（单向，改账号不回写名称）
     */
    handleGroupNameInput (val) {
      this.groupDialog.form.username = val
    },
    applyTestUserPreset () {
      Object.assign(this.groupDialog.form, {
        expDate: this.$moment().add(1, 'months').format('YYYY-MM-DD'),
        independentDeploy: false,
        serviceDomain: '',
        accountSize: 10,
        smsCount: 100,
        aiCount: 1000,
        advancedAi: true
      })
    },
    applyFormalUserPreset () {
      Object.assign(this.groupDialog.form, {
        expDate: this.$moment().add(1, 'years').format('YYYY-MM-DD'),
        accountSize: null,
        smsCount: null,
        aiCount: null,
        advancedAi: false
      })
    },
    /**
     * 添加集团：打开全字段弹框
     */
    addGroup () {
      this.groupDialog.form = {
        name: '',
        // 默认有效期：当前日期之后的一个月
        expDate: this.$moment().add(1, 'months').format('YYYY-MM-DD'),
        enabled: true,
        username: '',
        msgReceiverIds: [],
        longitude: '',
        latitude: '',
        independentDeploy: true,
        serviceDomain: '',
        accountSize: 10,
        aiCount: 1000,
        advancedAi: true, // 默认勾选高级AI-培训
        smsCount: 100
      }
      this.groupDialog.show = true
      this.loadReceivers()
    },
    /**
     * 集团消息接收人最多 10 人
     */
    handleGroupReceiverChange (val) {
      if (val.length > 10) {
        this.$message.warning('消息接收人最多选择 10 人')
        this.groupDialog.form.msgReceiverIds = val.slice(0, 10)
      }
    },
    /**
     * 编辑集团（列表铅笔）：打开整体编辑弹框
     */
    /**
     * 添加集团提交（传参与一般租户新增一致：username/expDate/state/配额/msgReceivers 必填）
     */
    submitGroup () {
      const form = this.groupDialog.form
      if (!form.name.trim()) {
        this.$message.warning('请输入集团租户名称')
        return
      }
      if (!form.expDate) {
        this.$message.warning('请选择有效期')
        return
      }
      if (!form.username.trim()) {
        this.$message.warning('请输入超管账号')
        return
      }
      // 独立部署时服务域名为必填项
      if (form.independentDeploy && !form.serviceDomain.trim()) {
        this.$message.warning('独立部署时请填写集团服务域名')
        return
      }
      if (form.accountSize === null || form.accountSize === undefined ||
        form.aiCount === null || form.aiCount === undefined ||
        form.smsCount === null || form.smsCount === undefined) {
        this.$message.warning('请填写配额上限')
        return
      }
      const data = {
        name: form.name.trim(),
        username: form.username.trim(),
        expDate: this.formatExpDate(form.expDate),
        state: form.enabled ? 1 : 2,
        accountSize: form.accountSize,
        aiCount: form.aiCount,
        smsCount: form.smsCount,
        // 高级AI：勾选培训传 '[0]'，未勾选传空字符串
        openAi: form.advancedAi ? '[0]' : '',
        // 消息接收人：sys_user.id 数组，最多 10 人
        msgReceivers: form.msgReceiverIds,
        independentDeployFlag: form.independentDeploy ? 1 : 0
      }
      // 独立部署的集团必填服务域名，留空则不推送同步
      if (form.independentDeploy) data.serviceDomain = form.serviceDomain.trim()
      const longitude = String(form.longitude || '').trim()
      const latitude = String(form.latitude || '').trim()
      if (longitude) data.longitude = longitude
      if (latitude) data.latitude = latitude
      this.groupDialog.loading = true
      this.$api.tenantGroup.addGroup(data).then(() => {
        this.groupDialog.loading = false
        this.$message.success('添加成功')
        this.groupDialog.show = false
        this.groupPage = 1
        this.getGroupList()
      }).catch(() => {
        this.groupDialog.loading = false
      })
    },
    /**
     * 打开整体编辑弹框（集团/节点/租户共用，属性数据取自当前面板 attrForm）
     */
    openEditDialog (node) {
      this.editDialog.node = {
        id: node.id,
        name: node.name,
        tenantType: node.tenantType,
        independentFlag: node.independentFlag || 0
      }
      const f = this.attrForm
      this.editDialog.form = {
        name: node.name || '',
        // 系统状态按接口返回的原始 state 回显：1 为启用，其余（2/3/4）均为禁用
        enabled: f.state === 1,
        // 集团/节点租户下拉回显：接口返回 2（禁用）时按 3（仅禁用该节点）展示
        state: f.state === 2 ? 3 : f.state,
        originState: f.state,
        adminAccount: f.adminAccount === '-' ? '' : f.adminAccount,
        notifyUserIds: (f.notifyUserIds || []).slice(),
        longitude: f.longitude || '',
        latitude: f.latitude || '',
        expireDate: f.expireDate === '-' ? '' : this.formatExpDate(f.expireDate),
        independentDeploy: f.independentDeploy === 1,
        serviceDomain: f.serviceDomain || '',
        accountTotal: f.accountTotal,
        aiTotal: f.aiTotal,
        advancedAi: f.advancedAi,
        smsTotal: f.smsTotal
      }
      this.editDialog.show = true
      // 已选接收人可能不在候选里（如已停用人员），合并进候选保证回显标签正确
      const extra = (f.notifyReceivers || []).map(r => {
        return { id: r.id || r.userId, name: r.name || r.username }
      }).filter(r => r.id !== null && r.id !== undefined)
      this.loadReceivers(extra)
    },
    /**
     * 消息接收人候选人员：GET /sys/user/options（全量 id+姓名，只加载一次）
     */
    loadReceivers (extra) {
      const merge = (list) => {
        const extras = (extra || []).filter(e => e.id && !list.some(o => o.id === e.id))
        this.receiverOptions = list.concat(extras)
      }
      if (this.receiversLoaded) {
        merge(this.receiverOptions)
        return
      }
      this.$api.organization.userOptions().then(res => {
        this.receiversLoaded = true
        const list = (res || []).map(item => {
          return { id: item.id, name: item.name }
        })
        merge(list)
      }).catch(() => {})
    },
    /**
     * 消息接收人最多 10 人
     */
    handleReceiverChange (val, form = this.editDialog.form) {
      if (val.length > 10) {
        this.$message.warning('消息接收人最多选择 10 人')
        form.notifyUserIds = val.slice(0, 10)
      }
    },
    /**
     * 保存整体编辑
     */
    submitEdit () {
      const node = this.editDialog.node
      if (!node) return
      const form = this.editDialog.form
      const full = this.editIsFull
      if (!form.name.trim()) {
        this.$message.warning('请输入名称')
        return
      }
      if (!form.adminAccount.trim()) {
        this.$message.warning('请输入超管账号')
        return
      }
      if (full) {
        if (!form.expireDate) {
          this.$message.warning('请选择有效期')
          return
        }
        // 独立部署时服务域名为必填项，未独立部署时不需要填写
        if (form.independentDeploy && !form.serviceDomain.trim()) {
          this.$message.warning('独立部署时请填写服务域名')
          return
        }
        if (form.accountTotal === null || form.accountTotal === undefined ||
          form.aiTotal === null || form.aiTotal === undefined ||
          form.smsTotal === null || form.smsTotal === undefined) {
          this.$message.warning('请填写配额上限')
          return
        }
      }
      const tenantId = node.id
      // 整体编辑统一走 /tenant/group/update，表单字段全量提交
      const data = {
        id: tenantId,
        name: form.name.trim(),
        username: form.adminAccount.trim(),
        // 系统状态：集团/节点直接传下拉值（1启用 3仅禁用该节点 4全部禁用）；
        // 下属租户按开关传 1/2，禁用类状态（3/4）原样回传
        state: node.tenantType === 1
          ? (form.enabled ? 1 : (form.originState && form.originState !== 1 ? form.originState : 2))
          : form.state,
        longitude: form.longitude.trim(),
        latitude: form.latitude.trim(),
        expDate: this.formatExpDate(form.expireDate),
        accountSize: form.accountTotal,
        aiCount: form.aiTotal,
        smsCount: form.smsTotal,
        // 高级AI：勾选培训传 '[0]'，未勾选传空字符串
        openAi: form.advancedAi ? '[0]' : '',
        independentDeployFlag: form.independentDeploy ? 1 : 0,
        serviceDomain: form.independentDeploy ? form.serviceDomain.trim() : '',
        // 消息接收人：sys_user.id 数组，最多 10 人
        msgReceivers: form.notifyUserIds
      }
      this.editDialog.loading = true
      this.$api.tenantGroup.editGroup(data).then(() => {
        this.editDialog.loading = false
        this.editDialog.show = false
        this.$message.success('保存成功')
        if (this.currentNode && this.currentNode.id === tenantId) {
          this.currentNode.name = form.name.trim()
        }
        this.loadGroupTree()
        this.getProductInfo(tenantId)
      }).catch(() => {
        this.editDialog.loading = false
      })
    },
    /**
     * 补充上级：新建一个节点，把当前根节点挂到该节点下
     */
    supplementParent () {
      this.parentDialog.form.name = ''
      this.parentDialog.show = true
    },
    submitParent () {
      this.$refs.parentForm.validate(valid => {
        if (!valid) return
        this.parentDialog.loading = true
        this.$api.tenantGroup.addParent({
          tenantId: this.currentGroup.id,
          name: this.parentDialog.form.name.trim()
        }).then(res => {
          this.parentDialog.loading = false
          this.$message.success('操作成功')
          this.parentDialog.show = false
          this.loadGroupTree()
        }).catch(() => {
          this.parentDialog.loading = false
        })
      })
    },
    /**
     * 名称联动超管账号：新建一般租户时名称变化超管账号始终跟随（单向，改账号不回写名称）
     */
    handleChildNameInput (val) {
      this.childDialog.form.username = val
    },
    selectChildType (target, source) {
      this.childTypePopoverVisible = false
      this.addChild(target, source)
    },
    /**
     * 新增下级
     */
    addChild (target, source) {
      this.childDialog.form = {
        target: target || 'tenant',
        source: source || 'new',
        tenantId: null,
        name: '',
        independent: false,
        enabled: true,
        username: '',
        longitude: '',
        latitude: '',
        // 默认有效期：当前日期之后的一个月（独立运营时展示）
        expDate: this.$moment().add(1, 'months').format('YYYY-MM-DD'),
        msgReceiverIds: [],
        independentDeploy: false,
        serviceDomain: '',
        openAiList: ['培训'], // 默认勾选高级AI-培训
        accountSize: 10,
        aiCount: 1000,
        smsCount: 100
      }
      this.childDialog.show = true
      if (this.childDialog.form.target === 'tenant') {
        this.getUnlinkedList('')
        this.loadReceivers()
      }
    },
    /**
     * 新增下级消息接收人最多 10 人
     */
    handleChildReceiverChange (val) {
      if (val.length > 10) {
        this.$message.warning('消息接收人最多选择 10 人')
        this.childDialog.form.msgReceiverIds = val.slice(0, 10)
      }
    },
    /**
     * 未挂接的一般租户列表（fuzzy 模糊搜索）
     */
    getUnlinkedList (fuzzy) {
      this.unlinkedLoading = true
      this.$api.tenantGroup.getUnattached({ fuzzy: fuzzy || '' }).then(res => {
        this.unlinkedLoading = false
        this.unlinkedTenants = res || []
      }).catch(() => {
        this.unlinkedLoading = false
      })
    },
    /**
     * 下拉远程搜索（防抖）
     */
    searchUnlinked: _.debounce(function (fuzzy) {
      this.getUnlinkedList(fuzzy)
    }, 500),
    submitChild () {
      const form = this.childDialog.form
      const data = { parentId: this.currentNode.id }
      if (form.target === 'node') {
        if (!form.name.trim()) {
          this.$message.warning('请输入节点名称')
          return
        }
        // 新增节点租户
        data.childType = 3
        data.source = 1
        data.name = form.name.trim()
        data.independentFlag = 0
      } else if (form.source === 'existing') {
        if (!form.tenantId) {
          this.$message.warning('请选择未挂接的一般租户')
          return
        }
        // 挂接已有一般租户（集团管理，资源配置按集团继承）
        data.childType = 1
        data.source = 2
        data.tenantId = form.tenantId
        data.independentFlag = 0
        if (form.longitude) data.longitude = form.longitude
        if (form.latitude) data.latitude = form.latitude
      } else {
        if (!form.name.trim()) {
          this.$message.warning('请输入一般租户名称')
          return
        }
        if (!form.username.trim()) {
          this.$message.warning('请输入超管账号')
          return
        }
        // 新建一般租户
        data.childType = 1
        data.source = 1
        data.name = form.name.trim()
        data.username = form.username.trim()
        data.state = form.enabled ? 1 : 2
        data.independentFlag = form.independent ? 1 : 0
        if (form.longitude) data.longitude = form.longitude
        if (form.latitude) data.latitude = form.latitude
        if (form.independent) {
          // 独立运营：有效期/配额必填
          if (!form.expDate) {
            this.$message.warning('请选择有效期')
            return
          }
          // 独立部署时服务域名为必填项
          if (form.independentDeploy && !form.serviceDomain.trim()) {
            this.$message.warning('独立部署时请填写服务域名')
            return
          }
          if (form.accountSize === null || form.accountSize === undefined ||
            form.aiCount === null || form.aiCount === undefined ||
            form.smsCount === null || form.smsCount === undefined) {
            this.$message.warning('请填写配额上限')
            return
          }
          data.expDate = this.formatExpDate(form.expDate)
          // 消息接收人：sys_user.id 数组，最多 10 人
          data.msgReceivers = form.msgReceiverIds
          data.accountSize = form.accountSize
          data.aiCount = form.aiCount
          data.smsCount = form.smsCount
          // 高级AI：勾选培训传 '[0]'，未勾选传空字符串
          data.openAi = form.openAiList.includes('培训') ? '[0]' : ''
          data.independentDeployFlag = form.independentDeploy ? 1 : 0
          // 独立部署时带服务域名；留空推送到平台默认服务
          if (form.independentDeploy) {
            data.serviceDomain = form.serviceDomain.trim()
          }
        }
      }
      this.childDialog.loading = true
      this.$api.tenantGroup.addChild(data).then(res => {
        this.childDialog.loading = false
        this.$message.success('操作成功')
        this.childDialog.show = false
        this.loadGroupTree()
      }).catch(() => {
        this.childDialog.loading = false
      })
    },
    /**
     * JSONP 请求（腾讯位置服务 WebService 接口浏览器端调用方式）
     */
    jsonp (url) {
      return new Promise((resolve, reject) => {
        const callback = `__qqMapCb${Date.now()}`
        const script = document.createElement('script')
        const cleanup = () => {
          delete window[callback]
          document.body.removeChild(script)
        }
        window[callback] = (data) => {
          cleanup()
          resolve(data)
        }
        script.onerror = () => {
          cleanup()
          reject(new Error('jsonp error'))
        }
        script.src = `${url}&output=jsonp&callback=${callback}`
        document.body.appendChild(script)
      })
    },
    /**
     * 根据名称匹配经纬度：调用腾讯位置服务关键词输入提示接口，按名称检索候选点位
     * key 配置在 .env 文件的 VUE_APP_TX_KEY
     */
    matchGeo (from) {
      this.geoTarget = from || 'child'
      let name = ''
      if (this.geoTarget === 'edit') {
        name = this.editDialog.form.name.trim()
        if (!name) {
          this.$message.warning('请先填写名称')
          return
        }
      } else if (this.geoTarget === 'group') {
        name = this.groupDialog.form.name.trim()
        if (!name) {
          this.$message.warning('请先填写名称')
          return
        }
      } else {
        const form = this.childDialog.form
        if (form.source === 'existing') {
          const tenant = this.unlinkedTenants.find(item => item.id === form.tenantId)
          name = tenant ? tenant.name : ''
          if (!name) {
            this.$message.warning('请先选择一般租户')
            return
          }
        } else {
          name = form.name.trim()
          if (!name) {
            this.$message.warning('请先填写名称')
            return
          }
        }
      }
      const key = process.env.VUE_APP_TX_KEY
      if (!key) {
        this.$message.warning('未配置腾讯位置服务 key（VUE_APP_TX_KEY）')
        return
      }
      this.geoDialog.name = name
      this.geoDialog.selected = null
      this.geoDialog.list = []
      this.geoDialog.loading = true
      this.geoDialog.show = true
      const url = 'https://apis.map.qq.com/ws/place/v1/suggestion' +
        `?keyword=${encodeURIComponent(name)}` +
        `&key=${key}`
      this.jsonp(url).then(res => {
        this.geoDialog.loading = false
        if (res.status === 0) {
          // 关键词提示返回候选点位列表，部分结果可能没有坐标，过滤掉
          this.geoDialog.list = (res.data || []).filter(item => item.location).map(item => {
            return {
              name: item.title,
              address: item.address || '',
              longitude: Number(item.location.lng).toFixed(6),
              latitude: Number(item.location.lat).toFixed(6)
            }
          })
          if (!this.geoDialog.list.length) {
            this.$message.warning('未匹配到该名称对应的点位')
          }
        } else {
          this.$message.warning(res.message || '点位匹配失败')
        }
      }).catch(() => {
        this.geoDialog.loading = false
        this.$message.warning('点位匹配失败，请检查网络后重试，或手动输入经纬度')
      })
    },
    /**
     * 使用此点位：把经纬度带回新增下级/整体编辑弹框
     */
    useGeoPoint () {
      const point = this.geoDialog.list[this.geoDialog.selected]
      if (!point) return
      const targetForm = this.geoTarget === 'edit'
        ? this.editDialog.form
        : (this.geoTarget === 'group' ? this.groupDialog.form : this.childDialog.form)
      targetForm.longitude = point.longitude
      targetForm.latitude = point.latitude
      this.geoDialog.show = false
    },
    /**
     * 删除节点
     */
    delNode () {
      // 有下属时不可删除
      if (this.currentNode._hasChildren || (this.currentNode.children && this.currentNode.children.length)) {
        this.$message.warning('该节点存在下属节点或租户，不可删除')
        return
      }
      this.$confirm(`此操作将删除「${this.currentNode.name}」, 是否继续?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$api.tenantGroup.delNode({ tenantId: this.currentNode.id }).then(res => {
          this.$message.success('删除成功')
          this.currentNode = null
          this.loadGroupTree()
        })
      }).catch(() => {})
    },
    /**
     * 运营状态变更（一般租户：0集团管理 1独立运营）：弹出确认弹框
     */
    openIndependentSwitch () {
      this.switchDialog.target = this.attrForm.independentFlag === 1 ? 0 : 1
      this.switchDialog.confirmed = false
      this.switchDialog.form = {
        reason: '',
        enabled: true,
        originState: 1,
        username: this.attrForm.adminAccount && this.attrForm.adminAccount !== '-' ? this.attrForm.adminAccount : (this.currentNode.name || ''),
        notifyUserIds: (this.attrForm.notifyUserIds || []).slice(),
        expireDate: this.attrForm.expireDate && this.attrForm.expireDate !== '-' ? this.attrForm.expireDate : '',
        longitude: this.attrForm.longitude || '',
        latitude: this.attrForm.latitude || '',
        // 历史独立配置：独立部署标识/服务域名
        independentDeploy: this.attrForm.independentDeploy === 1,
        serviceDomain: this.attrForm.serviceDomain || '',
        accountSize: this.attrForm.accountTotal || null,
        aiCount: this.attrForm.aiTotal || null,
        smsCount: this.attrForm.smsTotal || null,
        openAi: this.attrForm.advancedAi
      }
      if (this.switchDialog.target === 1) {
        const extra = (this.attrForm.notifyReceivers || []).map(r => {
          return { id: r.id || r.userId, name: r.name || r.username }
        }).filter(r => r.id !== null && r.id !== undefined)
        this.loadReceivers(extra)
      }
      this.switchDialog.show = true
    },
    /**
     * 弹框关闭且未确认时，还原当前运营方式
     */
    cancelSwitch () {
      if (!this.switchDialog.confirmed && this.currentNode) {
        this.attrForm.independentFlag = this.currentNode.independentFlag || 0
      }
    },
    /**
     * 确认切换运营状态
     */
    submitSwitch () {
      const form = this.switchDialog.form
      const data = {
        tenantId: this.currentNode.id,
        independentFlag: this.switchDialog.target,
        reason: form.reason.trim()
      }
      if (this.switchDialog.target === 1) {
        if (!form.username.trim()) {
          this.$message.warning('请输入超管账号')
          return
        }
        if (!form.expireDate) {
          this.$message.warning('请选择有效期')
          return
        }
        // 独立部署时服务域名为必填项
        if (form.independentDeploy && !form.serviceDomain.trim()) {
          this.$message.warning('独立部署时请填写服务域名')
          return
        }
        if (form.accountSize === null || form.accountSize === undefined ||
          form.aiCount === null || form.aiCount === undefined ||
          form.smsCount === null || form.smsCount === undefined) {
          this.$message.warning('请填写配额上限')
          return
        }
        data.username = form.username.trim()
        data.expDate = this.formatExpDate(form.expireDate)
        if (form.longitude.trim()) data.longitude = form.longitude.trim()
        if (form.latitude.trim()) data.latitude = form.latitude.trim()
        data.independentDeployFlag = form.independentDeploy ? 1 : 0
        if (form.independentDeploy) data.serviceDomain = form.serviceDomain.trim()
        data.accountSize = form.accountSize
        data.aiCount = form.aiCount
        data.smsCount = form.smsCount
        data.state = form.enabled ? 1 : (form.originState && form.originState !== 1 ? form.originState : 2)
        data.msgReceivers = form.notifyUserIds
        // 高级AI：勾选培训传 '[0]'，未勾选传空字符串
        data.openAi = form.openAi ? '[0]' : ''
      }
      this.switchDialog.loading = true
      this.$api.tenantGroup.updateProperty(data).then(res => {
        this.switchDialog.loading = false
        this.switchDialog.confirmed = true
        this.switchDialog.show = false
        this.$message.success('切换成功')
        this.attrForm.independentFlag = this.switchDialog.target
        this.loadGroupTree()
      }).catch(() => {
        this.switchDialog.loading = false
      })
    },
    /**
     * 调整所属节点（限当前集团内，上级只能选集团根或节点租户）
     */
    changeParent () {
      this.moveDialog.tree = []
      this.moveDialog.targetId = null
      this.moveDialog.show = true
      // 查询当前集团内可选的上级节点树
      this.moveDialog.treeLoading = true
      this.$api.tenantGroup.getMoveTree({ nodeId: this.currentNode.id }).then(res => {
        this.moveDialog.treeLoading = false
        this.moveDialog.tree = res ? [res] : []
      }).catch(() => {
        this.moveDialog.treeLoading = false
      })
    },
    /**
     * 调整所属节点弹框：选择上级
     */
    handleMoveSelect (data) {
      if (data.id === this.currentNode.id) {
        this.$message.warning('不能选择自身作为上级节点')
        return
      }
      if (data.tenantType === 1) {
        this.$message.warning('只能选择集团或节点作为上级节点')
        return
      }
      this.moveDialog.targetId = data.id
    },
    /**
     * 确认调整
     */
    submitMove () {
      if (!this.moveDialog.targetId) {
        this.$message.warning('请选择要挂载到的上级节点')
        return
      }
      this.moveDialog.loading = true
      this.$api.tenantGroup.moveNode({
        id: this.currentNode.id,
        parentId: this.moveDialog.targetId
      }).then(res => {
        this.moveDialog.loading = false
        this.$message.success('调整成功')
        this.moveDialog.show = false
        this.loadGroupTree()
      }).catch(() => {
        this.moveDialog.loading = false
      })
    },
    /**
     * 转为一般租户（脱离集团，重新确认租户属性）
     */
    convertToTenant () {
      this.convertDialog.form = {
        reason: '',
        enabled: this.attrForm.state === 1,
        originState: this.attrForm.state,
        username: this.attrForm.adminAccount && this.attrForm.adminAccount !== '-' ? this.attrForm.adminAccount : (this.currentNode.name || ''),
        notifyUserIds: (this.attrForm.notifyUserIds || []).slice(),
        expireDate: this.attrForm.expireDate && this.attrForm.expireDate !== '-' ? this.attrForm.expireDate : '',
        accountSize: this.attrForm.accountTotal || null,
        aiCount: this.attrForm.aiTotal || null,
        smsCount: this.attrForm.smsTotal || null,
        openAi: this.attrForm.advancedAi,
        longitude: this.attrForm.longitude || '',
        latitude: this.attrForm.latitude || ''
      }
      const extra = (this.attrForm.notifyReceivers || []).map(r => {
        return { id: r.id || r.userId, name: r.name || r.username }
      }).filter(r => r.id !== null && r.id !== undefined)
      this.loadReceivers(extra)
      this.convertDialog.show = true
    },
    /**
     * 确认转为一般租户
     */
    submitConvert () {
      const form = this.convertDialog.form
      if (!form.username.trim()) {
        this.$message.warning('请输入超管账号')
        return
      }
      if (!form.expireDate) {
        this.$message.warning('请选择有效期')
        return
      }
      if (form.accountSize === null || form.accountSize === undefined ||
        form.aiCount === null || form.aiCount === undefined ||
        form.smsCount === null || form.smsCount === undefined) {
        this.$message.warning('请填写配额上限')
        return
      }
      if (!form.reason || !form.reason.trim()) {
        this.$message.warning('请输入操作原因')
        return
      }
      const data = {
        tenantId: this.currentNode.id,
        username: form.username.trim(),
        expDate: this.formatExpDate(form.expireDate),
        accountSize: form.accountSize,
        aiCount: form.aiCount,
        smsCount: form.smsCount,
        openAi: form.openAi ? '[0]' : '',
        state: form.enabled ? 1 : (form.originState && form.originState !== 1 ? form.originState : 2),
        msgReceivers: form.notifyUserIds,
        reason: form.reason.trim()
      }
      if (form.longitude.trim()) data.longitude = form.longitude.trim()
      if (form.latitude.trim()) data.latitude = form.latitude.trim()
      this.convertDialog.loading = true
      this.$api.tenantGroup.convertTenant(data).then(res => {
        this.convertDialog.loading = false
        this.$message.success('转换成功')
        this.convertDialog.show = false
        this.currentNode = null
        this.loadGroupTree()
      }).catch(() => {
        this.convertDialog.loading = false
      })
    },
    /**
     * 有效期统一转为 yyyy-MM-dd（接口只认该格式，回显值可能带时分秒）
     */
    formatExpDate (val) {
      if (!val) return ''
      if (val instanceof Date) {
        const m = String(val.getMonth() + 1).padStart(2, '0')
        const d = String(val.getDate()).padStart(2, '0')
        return `${val.getFullYear()}-${m}-${d}`
      }
      const match = String(val).match(/\d{4}-\d{2}-\d{2}/)
      return match ? match[0] : String(val)
    },
    /**
     * 查看操作历史
     */
    viewHistory () {
      this.historyDialog.pageNum = 1
      this.historyDialog.list = []
      this.historyDialog.total = 0
      this.historyDialog.show = true
      this.getHistoryList()
    },
    /**
     * 操作历史分页查询（包含下属对象）
     */
    getHistoryList () {
      this.historyDialog.loading = true
      this.$api.tenantGroup.getLogList({
        pageNum: this.historyDialog.pageNum,
        pageSize: this.historyDialog.pageSize,
        tenantId: this.currentNode.id,
        includeChildren: true
      }).then(res => {
        this.historyDialog.loading = false
        this.historyDialog.list = res.list || []
        this.historyDialog.total = res.total || 0
      }).catch(() => {
        this.historyDialog.loading = false
      })
    },
    /**
     * 操作历史翻页
     */
    handleHistoryPageChange (page) {
      this.historyDialog.pageNum = page
      this.getHistoryList()
    }
  },
  beforeCreate () {
  },
  created () {
  },
  beforeMount () {
  },
  mounted () {
    window.addEventListener('resize', this.measureTreeNameOverflow)
    this.getGroupList()
  },
  beforeUpdate () {
  },
  updated () {
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.measureTreeNameOverflow)
  },
  destroyed () {
  }
}
</script>

<style lang="scss" scoped>
.org-tree-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  ::v-deep .el-button {
    margin-left: 0;
  }
}
.tenant-action-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-left: auto;
  ::v-deep .el-button {
    margin-left: 0;
  }
}
.attr-header {
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 8px;
}
.tenant-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 24px;
}
.tenant-form-section {
  min-width: 0;
}
.selected-node-title {
  flex: 1;
  min-width: 0;
  gap: 12px;
}
.selected-node-name {
  min-width: 0;
  overflow-wrap: anywhere;
}
.selected-node-type {
  flex-shrink: 0;
}
.switch-tenant-form ::v-deep .el-form-item__content {
  line-height: 1.5;
}
.edit-section-title {
  display: block;
  font-size: 15px;
  margin-bottom: 12px;
  padding-left: 8px;
  border-left: 3px solid #3377FF;
  line-height: 1.2;
}
.convert-reason-section,
.switch-reason-section {
  grid-column: 1 / -1;
}
.convert-tenant-form .field-hint {
  margin: 4px 0 0;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}
.tenant-dialog-titlebar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 20px;
  padding-right: 48px;
}
.tenant-quick-config {
  display: flex;
  align-items: center;
  gap: 8px;
  .quick-config-test {
    color: #E6A23C;
  }
  .quick-config-prod {
    color: #3377FF;
  }
}
.group-add-form,
.group-edit-form {
  .field-hint {
    margin: 4px 0 0;
    color: #909399;
    font-size: 12px;
    line-height: 1.4;
  }
}
@media (max-width: 720px) {
  .tenant-form-grid {
    grid-template-columns: 1fr;
  }
  .operation-mode-block,
  .child-tenant-basic-full {
    grid-column: auto;
  }
}
.geo-action-form-item {
  position: relative;
}
::v-deep .geo-action-form-item .el-form-item__content {
  position: static !important;
}
.geo-action-button {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
}
.move-tree {
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  padding: 8px;
  max-height: 320px;
  overflow-y: auto;
}
.switch-tip {
  display: flex;
  align-items: center;
  background-color: #fdf6ec;
  color: #e6a23c;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 13px;
}
.geo-list {
  max-height: 400px;
  overflow-y: auto;
  .geo-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    padding: 14px 16px;
    margin-bottom: 12px;
    cursor: pointer;
    &.active {
      border-color: #409EFF;
      background-color: #ecf5ff;
    }
    .geo-address {
      font-size: 13px;
      color: #606266;
    }
    .geo-coord {
      font-size: 12px;
      color: #909399;
    }
  }
}
.section-title {
  font-weight: bold;
  font-size: 14px;
  border-left: 3px solid #409EFF;
  padding-left: 8px;
  margin: 14px 0 0;
}
.dialog-section-title {
  font-weight: bold;
  font-size: 14px;
  border-left: 3px solid #409EFF;
  padding-left: 8px;
  margin: 4px 0 16px;
}
.operation-mode-item {
  margin-bottom: 32px;
}
.operation-mode-title {
  display: block;
  margin-bottom: 8px;
}
.req {
  color: #F56C6C;
  font-style: normal;
  margin-right: 2px;
}
.group-tenant {
  box-sizing: border-box;
  overflow: hidden;
  .left {
    width: 22%;
    flex-shrink: 0;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    .group-list {
      flex: 1;
      height: 0;
      overflow-y: auto;
    }
    .group-item {
      position: relative;
      padding: 14px 16px;
      border-radius: 4px;
      border: 1px solid #E6E6E6;
      margin-bottom: 12px;
      cursor: pointer;
      &.active {
        border-color: #328FFF;
        &:before {
          content: '';
          position: absolute;
          right: 0;
          top: 0;
          width: 20px;
          height: 20px;
          background: #1774FF;
          clip-path: polygon(0 0, 100% 0, 100% 100%);
        }
      }
      .group-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        flex: 0 0 28px;
        margin-right: 10px;
        border-radius: 4px;
        color: #fff;
        background: #3377FF;
        font-size: 16px;
      }
      // 名称区占满剩余空间并可收缩，避免长名称遮住右侧过期标签
      .group-name {
        flex: 1;
        min-width: 0;
      }
      .expiry-tag {
        flex-shrink: 0;
      }
      .selected-check {
        position: absolute;
        top: 1px;
        right: 1px;
        color: #fff;
        font-size: 9px;
        font-weight: 700;
      }
    }
  }
  .middle {
    width: 28%;
    max-width: 36%;
    flex-shrink: 0;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    .el-tree {
      flex: 1;
      height: 0;
      overflow-y: auto;
    }
    .org-tree {
      ::v-deep .el-tree-node__content {
        height: 36px;
        padding-right: 8px;
        border-radius: 4px;
      }
      ::v-deep .el-tree-node__expand-icon {
        margin-right: 4px;
        color: #909399;
        font-size: 13px;
      }
      ::v-deep .el-tree-node__expand-icon.is-leaf {
        color: transparent;
        cursor: default;
      }
      .org-tree-label {
        flex: 1;
        min-width: 0;
        overflow: hidden;
      }
      .org-tree-icon {
        width: 18px;
        flex: 0 0 18px;
        margin-right: 8px;
        text-align: center;
        font-size: 16px;
      }
      .org-tree-name {
        display: block;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        font-size: 15px;
        line-height: 20px;
        white-space: nowrap;
      }
      .org-tree-name-text {
        display: inline-block;
        white-space: nowrap;
      }
      .org-tree-name.is-overflow:hover .org-tree-name-text {
        animation: org-tree-name-marquee 5s linear infinite alternate;
      }
      @media (prefers-reduced-motion: reduce) {
        .org-tree-name.is-overflow:hover .org-tree-name-text {
          animation: none;
        }
      }
    }
  }
.field-hint {
  margin: 4px 0 0;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}
  .right {
    flex: 1;
    min-width: 0;
    height: 100%;
    box-sizing: border-box;
    overflow-y: auto;
    .inherit-tip {
      display: flex;
      align-items: center;
      background-color: #F4F4F5;
      color: #909399;
      font-size: 12px;
      padding: 6px 12px;
      border-radius: 4px;
      margin: 8px 0 2px;
    }
    .attr-header {
      padding-bottom: 8px;
      .attr-title {
        font-weight: bold;
        font-size: 14px;
        border-left: 3px solid #409EFF;
        padding-left: 8px;
      }
    }
    .attr-item {
      display: flex;
      align-items: center;
      min-height: 40px;
      padding: 6px 0;
      border-bottom: 1px solid #F2F4F7;
      position: relative;
      color: #303133;
      .attr-label {
        width: 110px;
        color: #909399;
        font-size: 14px;
        flex-shrink: 0;
      }
      .attr-value {
        display: flex;
        align-items: center;
        flex: 1;
        min-width: 0;
      }
      .attr-value-text {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .attr-copy {
        flex: 0 0 auto;
        margin-left: 8px;
      }
      .usage-value {
        font-size: 14px;
      }
      .attr-link {
        position: absolute;
        right: 0;
      }
      &.attr-disabled {
        background: linear-gradient(to right, #FFFFFF 0%, #F5F7FA 60%);
        cursor: not-allowed;
      }
    }
  }
}
@keyframes org-tree-name-marquee {
  0%, 15% { transform: translateX(0); }
  85%, 100% { transform: translateX(var(--marquee-distance)); }
}
// 导入组织架构弹框
.import-tip {
  font-size: 13px;
  color: #909399;
  i {
    color: #E6A23C;
    margin-right: 4px;
  }
}
.import-upload {
  display: block;
  width: 100%;
  ::v-deep .el-upload {
    width: 100%;
  }
  ::v-deep .el-upload-dragger {
    width: 100%;
    height: auto;
    padding: 20px 16px;
  }
}
.import-file {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  background-color: #FAFBFC;
  font-size: 13px;
}
</style>
